import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CreditCard, Wallet, Banknote, ArrowRight, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Payments() {
  const copy = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <div data-testid="payments-page" className="max-w-4xl mx-auto px-6 lg:px-10 pt-32 pb-24">
      <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">Easy Drive Car Rental</p>
      <h1 className="mt-3 font-display text-5xl sm:text-6xl tracking-tight">
        Ways to <span className="italic gold-text">pay</span>
      </h1>
      <p className="mt-5 text-muted-foreground max-w-xl leading-relaxed">
        Choose whichever payment method suits you. Always include your booking
        reference (e.g. ED-1A2B3C) when paying by PayPal or Zelle.
      </p>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div data-testid="payment-option-card" className="bg-card border border-border rounded-2xl p-8 flex flex-col transition-transform duration-300 hover:-translate-y-1 hover:border-primary/40">
          <div className="w-11 h-11 rounded-full border border-primary/30 flex items-center justify-center">
            <CreditCard size={18} className="gold-text" />
          </div>
          <h2 className="mt-5 font-display text-2xl tracking-tight">Credit / Debit Card</h2>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">
            Visa, Mastercard and Amex accepted through our secure Stripe checkout.
            Instant confirmation.
          </p>
          <div className="mt-4 flex items-center gap-1.5">
            <span className="bg-white border border-border rounded px-1.5 py-0.5 text-[9px] italic font-extrabold tracking-tight text-[#1A1F71] leading-none">VISA</span>
            <span className="bg-white border border-border rounded px-1.5 py-0.5 flex items-center leading-none">
              <span className="w-2.5 h-2.5 rounded-full bg-[#EB001B] inline-block" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#F79E1B] inline-block -ml-1 opacity-90" />
            </span>
            <span className="bg-[#2E77BC] border border-border rounded px-1.5 py-0.5 text-[9px] font-extrabold tracking-tight text-white leading-none">AMEX</span>
          </div>
          <Link to="/fleet" className="mt-6">
            <Button data-testid="payment-card-button" className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui">
              Book & Pay by Card <ArrowRight size={15} className="ml-2" />
            </Button>
          </Link>
        </div>

        <div data-testid="payment-option-paypal" className="bg-card border border-border rounded-2xl p-8 flex flex-col transition-transform duration-300 hover:-translate-y-1 hover:border-primary/40">
          <div className="w-11 h-11 rounded-full border border-primary/30 flex items-center justify-center">
            <Wallet size={18} className="gold-text" />
          </div>
          <h2 className="mt-5 font-display text-2xl tracking-tight">PayPal</h2>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">
            Send payment securely through PayPal. Include your booking reference in the
            payment note — we confirm within a few hours.
          </p>
          <a href="https://www.paypal.com" target="_blank" rel="noopener noreferrer" className="mt-6 block">
            <Button data-testid="payment-paypal-button" variant="outline" className="w-full rounded-full border-primary/40 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground font-ui">
              Pay with PayPal <ExternalLink size={14} className="ml-2" />
            </Button>
          </a>
        </div>

        <div data-testid="payment-option-zelle" className="bg-card border border-border rounded-2xl p-8 flex flex-col transition-transform duration-300 hover:-translate-y-1 hover:border-primary/40">
          <div className="w-11 h-11 rounded-full border border-primary/30 flex items-center justify-center">
            <Banknote size={18} className="gold-text" />
          </div>
          <h2 className="mt-5 font-display text-2xl tracking-tight">Zelle</h2>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">
            Send payment via Zelle and include your booking reference in the memo.
          </p>
          <div className="mt-6 space-y-2">
            <button data-testid="payment-zelle-copy-email" onClick={() => copy("roxfam2509@gmail.com", "Zelle email")} className="w-full flex items-center justify-between rounded-full border border-border px-4 py-2.5 text-sm font-ui text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors duration-200">
              roxfam2509@gmail.com <Copy size={13} />
            </button>
            <button data-testid="payment-zelle-copy-phone" onClick={() => copy("+1 (347) 751-5251", "Zelle phone")} className="w-full flex items-center justify-between rounded-full border border-border px-4 py-2.5 text-sm font-ui text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors duration-200">
              +1 (347) 751-5251 <Copy size={13} />
            </button>
          </div>
        </div>
      </div>

      <div className="mt-12 bg-card border border-border rounded-2xl p-8">
        <p className="text-sm text-muted-foreground leading-relaxed">
          <span className="text-foreground font-ui font-semibold">How manual payments work:</span>{" "}
          book your vehicle and choose PayPal or Zelle at checkout. You'll receive a
          booking reference instantly — include it in your payment note. Once we verify
          the payment, your booking is confirmed by email with your digital rental
          contract. Cancellations within 24 hours of pickup are non-refundable.
        </p>
      </div>
    </div>
  );
}
